import React, { useRef } from 'react';
import clsx from 'clsx';
import { IconProps } from '../icon/interfaces';
import InternalIcon from '../icon/internal';
import styles from './styles.css.js';
import useFocusVisible from '../internal/hooks/focus-visible';
import { getBaseProps } from '../internal/base-component';
import { fireCancelableEvent, isPlainLeftClick } from '../internal/events';
import useForwardFocus from '../internal/hooks/forward-focus';
import { KeyCode } from '../internal/keycode';
import { LinkProps } from './interfaces';
import { InternalBaseComponentProps } from '../internal/hooks/use-base-component';
import mergeRefs from 'react-merge-refs';

const ICON_SIZES: Record<LinkProps.FontSize, IconProps.Size> = {
  'body-s': 'small',
  'body-m': 'normal',
  'heading-xs': 'normal',
  'heading-s': 'normal',
  'heading-m': 'normal',
  'heading-l': 'medium',
  'heading-xl': 'big',
  'display-l': 'large',
};

type InternalLinkProps = InternalBaseComponentProps &
  Omit<LinkProps, 'variant'> & {
    variant?: LinkProps['variant'] | 'top-navigation';
  };

const InternalLink = React.forwardRef(
  (
    {
      variant = 'secondary',
      fontSize = 'body-m',
      color = 'normal',
      external = false,
      target,
      href,
      rel,
      ariaLabel,
      externalIconAriaLabel,
      onFollow,
      children,
      __internalRootRef = null,
      ...props
    }: InternalLinkProps,
    ref: React.Ref<LinkProps.Ref>
  ) => {
    const isButton = !href;
    const focusVisible = useFocusVisible();
    const baseProps = getBaseProps(props);
    const anchorTarget = target ?? (external ? '_blank' : undefined);
    const anchorRel = rel ?? (anchorTarget === '_blank' ? 'noopener noreferrer' : undefined);

    const fireFollowEvent = (event: React.SyntheticEvent) => {
      fireCancelableEvent(onFollow, { href, external, target: anchorTarget }, event);
    };

    const handleLinkClick = (event: React.MouseEvent) => {
      if (isPlainLeftClick(event)) {
        fireFollowEvent(event);
      }
    };

    const handleButtonClick = (event: React.MouseEvent) => {
      fireFollowEvent(event);
    };

    const handleButtonKeyDown = (event: React.KeyboardEvent) => {
      // Prevent the page from scrolling down when spacebar is pressed.
      if (event.keyCode === KeyCode.space) {
        event.preventDefault();
      }
    };

    const handleButtonKeyUp = (event: React.KeyboardEvent) => {
      if (event.keyCode === KeyCode.space || event.keyCode === KeyCode.enter) {
        fireFollowEvent(event);
      }
    };

    const linkRef = useRef<HTMLElement>(null);
    useForwardFocus(ref, linkRef);

    const sharedProps = {
      ...focusVisible,
      ...baseProps,
      // https://github.com/microsoft/TypeScript/issues/36659
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref: mergeRefs([linkRef as any, __internalRootRef]),
      className: clsx(
        styles.link,
        baseProps.className,
        variant === 'top-navigation' && styles['top-navigation'],
        styles[`weight-${variant === 'secondary' ? 'normal' : 'bold'}`],
        styles[`font-size-${variant === 'info' ? 'body-s' : fontSize}`],
        styles[`color-${variant === 'info' ? 'normal' : color}`]
      ),
      'aria-label': ariaLabel,
    };

    const content = (
      <>
        {children}
        {external && (
          <>
            {' '}
            <span
              className={styles.icon}
              aria-label={externalIconAriaLabel}
              role={externalIconAriaLabel ? 'img' : undefined}
            >
              <InternalIcon name="external" size={variant === 'info' ? 'small' : ICON_SIZES[fontSize]} />
            </span>
          </>
        )}
      </>
    );

    if (isButton) {
      return (
        <a
          {...sharedProps}
          role="button"
          tabIndex={0}
          onKeyDown={handleButtonKeyDown}
          onKeyUp={handleButtonKeyUp}
          onClick={handleButtonClick}
        >
          {content}
        </a>
      );
    }

    return (
      // we dynamically set proper rel in the code above
      // eslint-disable-next-line react/jsx-no-target-blank
      <a {...sharedProps} target={anchorTarget} rel={anchorRel} href={href} onClick={handleLinkClick}>
        {content}
      </a>
    );
  }
);

export default InternalLink;
